import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ShoppingBag, X } from 'lucide-react';
import { previewPurchases, recentPurchases } from '../data/recentPurchases';
const INITIAL_DELAY_MS = 5000;
const VISIBLE_MS = 5000;
const GAP_MS = 4000;
export function PurchaseNotification() {
  const purchases = useMemo(() => recentPurchases.length > 0 ? recentPurchases : isEditorPreview() ? previewPurchases : [], []);
  const [index, setIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (isDismissed || purchases.length === 0) return;
    const delay = isVisible ? VISIBLE_MS : hasStarted ? GAP_MS : INITIAL_DELAY_MS;
    const timer = window.setTimeout(() => {
      if (isVisible) {
        setIsVisible(false);
        setIndex(current => (current + 1) % purchases.length);
      } else {
        setHasStarted(true);
        setIsVisible(true);
      }
    }, delay);
    return () => window.clearTimeout(timer);
  }, [isVisible, isDismissed, hasStarted, purchases.length]);
  if (isDismissed || purchases.length === 0) return null;
  const purchase = purchases[index];
  const minutesLabel = `${purchase.minutesAgo} minute${purchase.minutesAgo === 1 ? '' : 's'} ago`;
  return <div className="fixed bottom-4 left-4 z-40 w-[320px] max-w-[calc(100vw-2rem)] pointer-events-none" aria-live="polite" data-id="element-239" data-mp="19nbsroo2p7eh">
      <AnimatePresence data-id="element-240" data-mp="zrqwm75or1cn">
        {isVisible && <motion.div key={`${purchase.firstName}-${purchase.city}-${index}`} initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 16,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 8,
        scale: 0.98
      }} transition={{
        duration: 0.28,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto relative flex items-center gap-3.5 bg-white rounded-2xl border border-navy/5 shadow-xl shadow-navy/20 py-3 pl-3 pr-9 overflow-hidden" role="status" data-id="element-241" data-mp="zt1yg1fklz14">
            <span className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full bg-cta" aria-hidden="true" data-id="element-242" data-mp="2np9mk8c6zkhz" />

            <div className="relative shrink-0 w-11 h-11 rounded-xl bg-navy text-cta flex items-center justify-center" data-id="element-243" data-mp="2jll9ja4sjwpc">
              <ShoppingBag size={19} strokeWidth={2.2} data-id="element-244" data-mp="3beu4qp0iudq7" />
            </div>

            <div className="min-w-0 flex-1" data-id="element-245" data-mp="3bcrfi7770omj">
              <p className="flex items-center gap-1.5 text-sm text-navy leading-5 whitespace-nowrap" data-id="element-246" data-mp="37c7ir4qj7n0x">
                <img src={`https://flagcdn.com/${purchase.countryCode}.svg`} alt={`${purchase.country} flag`} className="w-[18px] h-[13px] rounded-[2px] object-cover shrink-0 ring-1 ring-black/5" data-id="element-247" data-mp="32culdq21axk3" />
                <span className="font-bold" data-id="element-248" data-mp="6gyfeiy5ji01">{purchase.firstName}</span>
                <span className="text-navy/70 truncate" data-id="element-249" data-mp="6elohgaf1xau">
                  from {purchase.city}, {purchase.country}
                </span>
              </p>
              <p className="text-sm text-navy/70 leading-5" data-id="element-250" data-mp="fh4rq1i46bxm">
                purchased{' '}
                <span className="font-semibold text-navy" data-id="element-251" data-mp="k8cyeps27j73">Scuba Diving Instructor Ready</span>
              </p>
              <p className="flex items-center gap-1.5 text-xs text-slate-500 leading-4 mt-1" data-id="element-252" data-mp="p64rhn4fxbcv">
                <span className="w-1.5 h-1.5 rounded-full bg-green-600" aria-hidden="true" data-id="element-253" data-mp="u57vr5cljdjs" />
                {minutesLabel}
              </p>
            </div>

            <button type="button" onClick={() => setIsDismissed(true)} className="absolute top-2 right-2 w-6 h-6 rounded-full text-slate-400 hover:text-navy hover:bg-mist flex items-center justify-center transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean" aria-label="Close notification" data-id="element-254" data-mp="136z0a6vehec2">
              <X size={13} data-id="element-255" data-mp="25n3h7r788fur" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function isEditorPreview(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}